import { createContext, useCallback, useContext, useMemo, useState, type ReactNode } from 'react';

import { SupportContactModal } from '~/components/support/SupportContactModal';
import { SupportFab } from '~/components/support/SupportFab';

type SupportContextType = {
  openSupport: () => void;
};

const SupportContext = createContext<SupportContextType | undefined>(undefined);

type SupportProviderProps = {
  children: ReactNode;
  userEmail: string;
  userName: string;
};

export function SupportProvider({ children, userEmail, userName }: SupportProviderProps) {
  const [isOpen, setIsOpen] = useState(false);

  const openSupport = useCallback(() => setIsOpen(true), []);
  const closeSupport = useCallback(() => setIsOpen(false), []);

  const contextValue = useMemo(() => ({ openSupport }), [openSupport]);

  return (
    <SupportContext.Provider value={contextValue}>
      {children}
      <SupportFab onClick={openSupport} />
      <SupportContactModal
        isOpen={isOpen}
        onClose={closeSupport}
        userEmail={userEmail}
        userName={userName}
      />
    </SupportContext.Provider>
  );
}

export function useSupport(): SupportContextType {
  const context = useContext(SupportContext);
  if (!context) {
    throw new Error('useSupport must be used within SupportProvider');
  }
  return context;
}
